import { useEffect, useState, type ReactNode } from 'react';
import type { PublicGuest, PublicTrack, SearchResponse, Toast } from '@karozik/shared';
import { api, cn } from './api.js';
import { useDebounced, type FloatingReaction } from './useParty.js';

/** Album artwork with a neon gradient fallback. */
export function Artwork({ track, className, rounded = 'rounded-2xl' }: { track: Pick<PublicTrack, 'artworkUrl' | 'title'> | null; className?: string; rounded?: string }) {
  const [failed, setFailed] = useState(false);
  const url = track?.artworkUrl && !failed ? track.artworkUrl : null;
  return (
    <div className={cn('relative overflow-hidden bg-surface-2 shrink-0', rounded, className)}>
      {url ? (
        <img src={url} alt="" loading="lazy" onError={() => setFailed(true)} className="h-full w-full object-cover" />
      ) : (
        <div className="bg-gradient-neon flex h-full w-full items-center justify-center text-[40%] opacity-80">♪</div>
      )}
    </div>
  );
}

export function Avatar({ guest, size = 'md', className }: { guest: Pick<PublicGuest, 'emoji' | 'color'> | null; size?: 'sm' | 'md' | 'lg' | 'xl'; className?: string }) {
  const dims = { sm: 'h-6 w-6 text-sm', md: 'h-9 w-9 text-lg', lg: 'h-14 w-14 text-3xl', xl: 'h-24 w-24 text-5xl' }[size];
  return (
    <span
      className={cn('inline-flex shrink-0 items-center justify-center rounded-full', dims, className)}
      style={{ background: guest ? `${guest.color}33` : '#ffffff14', boxShadow: guest ? `inset 0 0 0 2px ${guest.color}aa` : undefined }}
    >
      {guest?.emoji ?? '🤖'}
    </span>
  );
}

export function Equalizer({ playing = true, className, bars = 4 }: { playing?: boolean; className?: string; bars?: number }) {
  return (
    <span className={cn('inline-flex h-4 items-end gap-[3px]', className)} aria-hidden>
      {Array.from({ length: bars }, (_, i) => (
        <span
          key={i}
          className={cn('w-[3px] origin-bottom rounded-full bg-current', playing ? 'animate-eq' : 'scale-y-25')}
          style={{ height: '100%', animationDelay: `${i * 0.17}s`, animationDuration: `${0.6 + (i % 3) * 0.2}s` }}
        />
      ))}
    </span>
  );
}

export function Spinner({ className }: { className?: string }) {
  return <span className={cn('inline-block h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-white', className)} aria-label="Chargement" />;
}

export function ToastStack({ toasts, onDismiss }: { toasts: (Toast & { key: number })[]; onDismiss(key: number): void }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-50 flex flex-col items-center gap-2 px-4" aria-live="polite">
      {toasts.map((t) => (
        <button
          key={t.key}
          onClick={() => onDismiss(t.key)}
          className={cn(
            'animate-pop pointer-events-auto w-full max-w-md rounded-2xl px-4 py-3 text-left text-sm font-medium shadow-2xl',
            t.kind === 'error' && 'bg-danger text-white',
            t.kind === 'success' && 'bg-lime text-night',
            t.kind === 'info' && 'glass text-ink',
          )}
        >
          {t.message}
        </button>
      ))}
    </div>
  );
}

/** Emojis rising from the bottom of the container. */
export function FloatingReactions({ reactions, showNames = false, size = 'text-4xl' }: { reactions: FloatingReaction[]; showNames?: boolean; size?: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {reactions.map((r) => (
        <div
          key={r.key}
          className="animate-float-up absolute bottom-0 flex flex-col items-center"
          style={{ left: `${r.left}%`, ['--drift' as string]: `${r.drift}px` }}
        >
          <span className={size}>{r.emoji}</span>
          {showNames && r.guest && (
            <span className="mt-1 rounded-full px-2 py-0.5 text-sm font-semibold text-white/90" style={{ background: `${r.guest.color}66` }}>
              {r.guest.pseudo}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

/** Live catalog search (debounced). `auth` tells the API who is asking. */
export function useTrackSearch(query: string, auth: { joinToken?: string; kioskGuestToken?: string | null }) {
  const q = useDebounced(query.trim(), 450);
  const [result, setResult] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (q.length < 2) {
      setResult(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ q, ...(auth.joinToken ? { joinToken: auth.joinToken } : {}) });
    api<SearchResponse>(`/api/search?${params}`, {
      headers: auth.kioskGuestToken ? { 'x-kiosk-guest': auth.kioskGuestToken } : {},
    })
      .then((r) => !cancelled && (setResult(r), setError(null)))
      .catch((e: Error) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [q, auth.joinToken, auth.kioskGuestToken]);
  return { result, loading: loading || (query.trim().length >= 2 && q !== query.trim()), error };
}

export function TrackLine({ track, right, sub, onClick, artSize = 'h-12 w-12' }: { track: PublicTrack; right?: ReactNode; sub?: ReactNode; onClick?: () => void; artSize?: string }) {
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp onClick={onClick} className={cn('flex w-full items-center gap-3 text-left', onClick && 'active:scale-[0.99] transition')}>
      <Artwork track={track} className={artSize} rounded="rounded-xl" />
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold">{track.title}</div>
        <div className="truncate text-sm text-ink-dim">
          {track.artist}
          {track.year ? <span className="text-ink-faint"> · {track.year}</span> : null}
          {track.explicit ? <span className="ml-1 rounded bg-white/15 px-1 text-[10px] font-bold">E</span> : null}
        </div>
        {sub}
      </div>
      {right}
    </Comp>
  );
}

export function formatClock(ms: number): string {
  return new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** « 820 ko », « 12,4 Mo ». */
export function formatBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} ko`;
  const mb = n / (1024 * 1024);
  return `${mb.toLocaleString('fr-FR', { maximumFractionDigits: mb < 100 ? 1 : 0 })} Mo`;
}

export function formatCountdown(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 24) return `${Math.floor(h / 24)} j ${h % 24} h`;
  if (h > 0) return `${h} h ${String(m).padStart(2, '0')}`;
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}
